import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography } from "@mui/material";

import AlertDialog from "../../common/AlertDialog";
import { Loader } from "../../common/loader";
import { Button } from "../../common/customDesignedComponents/button";
import { encryptPayload, getObjLength } from "../../helpers/utils";
import { attachEnhancedToCompleteUpgradeFlags } from "../../helpers/generatePropertySecurityQuestions";
import { mapCreateUserApiUrl } from "../../for-service-provider/createUserApiPaths";
import { postAPIPayload } from "../../../apis/postApi";
import { rememberCarInEscrowPinSkip } from "../../for-service-provider/carPortfolioStatus";
import { userSigupAction } from "../../../redux/slices/user/userSignUpSlice";

/**
 * Servicer-only confirmation for the 5-year paid-at-closing path.
 * Creates an unpaid closing invoice, emails the logged-in SP, and defers
 * activation until an admin marks the invoice paid.
 */
const PaidAtClosingConfirm = ({
  onChangeModalScreen,
  onCloseModalHandler,
  completedProcess,
  finalAmount,
  planDetails,
  user_id,
  selectedData = {},
  isTailwindComponent = false,
  isServiceProvider = false,
  backScreen = "categoryWisePricing",
}) => {
  const dispatch = useDispatch();
  const { selectedUserID, selectedProperty } = useSelector(
    (state) => state.properties,
  );
  const {
    mailingAddress,
    mailingAddressLoader,
    createAccResponse,
    propertyIds,
    properties,
    verifiedPropertiesList,
  } = useSelector((state) => state.signUpUser);

  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);
  const [alreadyInvoiced, setAlreadyInvoiced] = useState(false);
  const [inEscrow] = useState(() => {
    try {
      return (
        JSON.parse(localStorage.getItem("carPortfolioPrefill") || "{}")
          .enrollment_kind === "in_escrow"
      );
    } catch {
      return false;
    }
  });

  // "Buyer to Pay at Closing" on the plans page (card 3853).
  const [buyerPays] = useState(() => {
    try {
      return (
        JSON.parse(localStorage.getItem("carEnrollment") || "{}")
          .payment_timing === "buyer_pays_at_closing"
      );
    } catch {
      return false;
    }
  });

  const resolvedUserId =
    user_id || selectedUserID || createAccResponse?.data?.user_id || null;

  // Prefer the Owner Verification prefetch; refill only if Redux is still empty.
  useEffect(() => {
    if (!resolvedUserId || mailingAddressLoader) {
      return;
    }
    if (mailingAddress?.success && getObjLength(mailingAddress?.data)) {
      return;
    }
    dispatch(
      userSigupAction.getMailingAddress({
        url: "get-mailing-address",
        data: {
          user_id: resolvedUserId,
          step: 2,
          step_key: "GET_MAILING_ADDRESS",
        },
      }),
    );
  }, [
    dispatch,
    mailingAddress?.data,
    mailingAddress?.success,
    mailingAddressLoader,
    resolvedUserId,
  ]);

  // SP fallback when selected property / mailing address are empty.
  const firstVerifiedProperty = useMemo(() => {
    if (!isServiceProvider) {
      return null;
    }
    const list = Array.isArray(verifiedPropertiesList?.data)
      ? verifiedPropertiesList.data
      : [];
    return list.find((row) => row && !row?.not_added) || list[0] || null;
  }, [isServiceProvider, verifiedPropertiesList?.data]);

  const property = useMemo(() => {
    if (getObjLength(selectedProperty)) {
      return selectedProperty;
    }
    if (getObjLength(selectedData)) {
      return selectedData;
    }
    if (isServiceProvider && getObjLength(firstVerifiedProperty)) {
      return firstVerifiedProperty;
    }
    return {};
  }, [
    firstVerifiedProperty,
    isServiceProvider,
    selectedData,
    selectedProperty,
  ]);

  // Same multi-property list as get-final-amount / card checkout.
  const resolvedPropertyIds = useMemo(() => {
    const fromRedux = Array.isArray(propertyIds)
      ? propertyIds.filter(Boolean)
      : [];
    if (fromRedux.length > 0) {
      return fromRedux;
    }

    const fromRows = Array.isArray(properties)
      ? properties.map((row) => row?.property_id).filter(Boolean)
      : [];
    if (fromRows.length > 0) {
      return fromRows;
    }

    try {
      const planData = JSON.parse(localStorage.getItem("Plan_Data") || "{}");
      const fromPlanData = Array.isArray(planData?.property_id)
        ? planData.property_id.filter(Boolean)
        : Array.isArray(planData?.property_ids)
          ? planData.property_ids.map((row) => row?.property_id).filter(Boolean)
          : [];
      if (fromPlanData.length > 0) {
        return fromPlanData;
      }
    } catch {
      // ignore invalid Plan_Data
    }

    const singleId =
      property?.property_id || property?.id || property?.propertyId;
    return singleId ? [singleId] : [];
  }, [properties, property, propertyIds]);

  const billing = useMemo(() => {
    const mailing = mailingAddress?.data || {};
    const mailingAddressBlock =
      mailing?.address && typeof mailing.address === "object"
        ? mailing.address
        : mailing;
    const hasMailingValues = Boolean(
      mailing?.first_name ||
      mailing?.last_name ||
      mailingAddressBlock?.address ||
      mailing?.address ||
      mailingAddressBlock?.city ||
      mailing?.city ||
      mailingAddressBlock?.state ||
      mailing?.state ||
      mailingAddressBlock?.zip ||
      mailing?.zip,
    );
    const fallbackProperty =
      isServiceProvider &&
      !hasMailingValues &&
      getObjLength(firstVerifiedProperty)
        ? firstVerifiedProperty
        : null;
    const sourceProperty = getObjLength(property)
      ? property
      : fallbackProperty || {};

    const details =
      sourceProperty?.property_details || sourceProperty?.propertyDetails || {};
    const generalInfo =
      details?.GeneralInfo || details?.generalInfo || details || {};

    return {
      first_name:
        mailing.first_name ||
        sourceProperty?.owner?.[0]?.first_name ||
        sourceProperty?.first_name ||
        "Service",
      last_name:
        mailing.last_name ||
        sourceProperty?.owner?.[0]?.last_name ||
        sourceProperty?.last_name ||
        "Provider",
      address:
        mailingAddressBlock?.address ||
        (typeof mailing.address === "string" ? mailing.address : "") ||
        sourceProperty?.address ||
        generalInfo?.Address ||
        "N/A",
      city:
        mailingAddressBlock?.city ||
        mailing?.city ||
        sourceProperty?.city ||
        generalInfo?.City ||
        generalInfo?.city ||
        "N/A",
      state:
        mailingAddressBlock?.state ||
        mailing?.state ||
        sourceProperty?.state ||
        sourceProperty?.state_code ||
        sourceProperty?.mm_fips_state_code ||
        generalInfo?.State ||
        generalInfo?.state ||
        "",
      zip:
        mailingAddressBlock?.zip ||
        mailing?.zip ||
        sourceProperty?.zip ||
        generalInfo?.Zip ||
        generalInfo?.zip ||
        "00000",
    };
  }, [
    firstVerifiedProperty,
    isServiceProvider,
    mailingAddress?.data,
    property,
  ]);

  const totalDisplay = finalAmount?.total ?? "0.00";
  const termMonths = finalAmount?.term_months || 60;
  const pocRecording = Boolean(finalAmount?.poc_recording);
  const planId = planDetails?.plan_id || finalAmount?.plan_id;

  const handleConfirm = async () => {
    if (!resolvedUserId) {
      setErrorMessage("Client user is required.");
      return;
    }
    if (!planId) {
      setErrorMessage("Subscription plan is required.");
      return;
    }
    if (!resolvedPropertyIds.length) {
      setErrorMessage("Property is required.");
      return;
    }
    if (!String(billing.state || "").trim()) {
      if (mailingAddressLoader) {
        setErrorMessage(
          "Loading billing details… please wait a moment and try again.",
        );
        return;
      }
      setErrorMessage(
        "Billing state is missing for this client/property. Add the property state and try again.",
      );
      return;
    }

    setSubmitting(true);
    setErrorMessage("");

    const promoCode = localStorage.getItem("car_portfolio_id")
      ? null
      : localStorage.getItem("promoCode") || finalAmount?.promo_code || null;

    let storedCar = {};
    try {
      storedCar = JSON.parse(localStorage.getItem("carEnrollment") || "{}");
    } catch {
      storedCar = {};
    }
    const payload = {
      user_id: resolvedUserId,
      plan: planId,
      property_ids: resolvedPropertyIds,
      step: 4,
      step_key: "PAYMENT",
      payment_mode: "paid_at_closing",
      payment_plan:
        finalAmount?.payment_plan ||
        storedCar.payment_plan ||
        "paid_at_closing_5yr",
      ...(storedCar.revenue_share_choice
        ? { revenue_share_choice: storedCar.revenue_share_choice }
        : {}),
      ...(storedCar.car_agent_property_id
        ? { car_agent_property_id: storedCar.car_agent_property_id }
        : {}),
      ...(Array.isArray(storedCar.car_agent_property_ids)
        ? { car_agent_property_ids: storedCar.car_agent_property_ids }
        : {}),
      poc_recording: pocRecording,
      first_name: billing.first_name,
      last_name: billing.last_name,
      address: billing.address,
      city: billing.city,
      state: billing.state,
      zip: billing.zip,
      ...(promoCode ? { promo_code: promoCode } : {}),
    };

    const paymentPayload = attachEnhancedToCompleteUpgradeFlags(payload, {
      property: selectedProperty || selectedData,
    });

    try {
      const url = mapCreateUserApiUrl(
        `admin/users/${resolvedUserId}/post-payment`,
        isServiceProvider,
      );
      const response = await postAPIPayload({
        url,
        data: paymentPayload,
        token: true,
      });

      if (response?.success) {
        rememberCarInEscrowPinSkip();
        localStorage.removeItem("car_portfolio_id");
        localStorage.removeItem("carEnrollment");
        localStorage.removeItem("carPortfolioPrefill");
        setSuccessOpen(true);
        return;
      }

      const failureMessage =
        response?.message ||
        response?.data?.message ||
        "Unable to create the closing invoice.";

      // The invoice landed on an earlier attempt, so let the servicer finish
      // setup instead of dead-ending here.
      if (/already exists/i.test(failureMessage)) {
        rememberCarInEscrowPinSkip();
        setAlreadyInvoiced(true);
        setSuccessOpen(true);
        return;
      }

      setErrorMessage(failureMessage);
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to create the closing invoice.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    setSuccessOpen(false);
    // Keep Complete Protection category so the next security-PIN screen mounts.
    try {
      if (!localStorage.getItem("category")) {
        localStorage.setItem("category", encryptPayload("complete_protection"));
      }
      localStorage.removeItem("promoCode");
      localStorage.removeItem("referralCode");
    } catch {
      // ignore storage failures
    }
    dispatch(
      userSigupAction.setPropertiesId({
        propertyIds: {},
        properties: {},
        ids: {},
      }),
    );
    // Invoice created → continue wizard (security PIN), do not bail to workspace.
    onCloseModalHandler?.("success");
  };

  return (
    <Box className="tailwind_component bg-white px-5 pt-4 pb-5">
      <Typography variant="h6" className="mb-2 font-semibold">
        Create closing invoice
      </Typography>
      <Typography variant="body2" color="text.secondary" className="mb-4">
        {inEscrow
          ? buyerPays
            ? "This creates an invoice for the selected protection plan, payable by the Buyer through escrow at closing. The Escrow Officer will receive the invoice, and EquityProtect will mark it as paid once payment is received."
            : "This creates an invoice for the selected protection plan, payable from the Agent's commission at closing. The Escrow Officer will receive the invoice, and EquityProtect will mark it as paid once payment is received."
          : `This creates a ${termMonths}-month paid-at-closing invoice (Net 30) and emails it to you. UCC work can proceed; EquityProtect marks the invoice paid when funds are received.`}
      </Typography>

      <Box className="rounded-xl border border-gray-200 bg-gray-50 p-4 mb-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span>Plan</span>
          <span className="font-semibold">
            {inEscrow
              ? `${Math.round(termMonths / 12)}-Year Complete Protection Package`
              : termMonths
                ? `${termMonths / 12} year paid at closing`
                : "Paid at closing"}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span>Term</span>
          <span className="font-semibold">
            {inEscrow ? "Paid at Closing" : `${termMonths} months`}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span>Recording deposit</span>
          <span className="font-semibold">
            {pocRecording ? "POC (servicer pays)" : "Included in total"}
          </span>
        </div>
        <div className="flex justify-between text-base pt-2 border-t border-gray-200">
          <span className="font-semibold">Total due at closing</span>
          <span className="font-bold text-[#0e7490]">
            {String(totalDisplay).startsWith("$")
              ? totalDisplay
              : `$${totalDisplay}`}
          </span>
        </div>
      </Box>

      {errorMessage ? (
        <Typography variant="body2" color="error" className="mb-3">
          {errorMessage}
        </Typography>
      ) : null}

      <div className="mt-2 flex flex-col-reverse items-center justify-center gap-3 sm:flex-row">
        <Button
          type="button"
          variant="outline"
          onClick={() => onChangeModalScreen?.(backScreen)}
          disabled={submitting}
          className="w-full rounded-full border-2 px-8 py-2.5 font-medium text-dark hover:bg-muted/50 sm:w-auto"
        >
          Back
        </Button>
        <Button
          type="button"
          onClick={handleConfirm}
          disabled={submitting}
          className="w-full rounded-full bg-gradient-to-r from-secondary to-cyan-400 px-8 py-2.5 font-semibold text-white shadow-md ring-2 ring-secondary/20 ring-offset-2 transition-all hover:from-secondary/90 hover:to-cyan-400/90 hover:shadow-lg touch-manipulation sm:w-auto"
        >
          {submitting
            ? "Creating…"
            : inEscrow
              ? "Create & Send Invoice"
              : "Create & send invoice"}
        </Button>
      </div>
      {inEscrow ? (
        <p className="mt-2 text-center text-xs text-muted-foreground">
          Invoice will be sent to Escrow.
        </p>
      ) : null}

      <Loader open={submitting} />

      {successOpen ? (
        <AlertDialog
          isTailwindComponent={isTailwindComponent}
          type="Success"
          openPopup={true}
          onClose={true}
          onCloseFun={handleSuccessClose}
          mesage={
            alreadyInvoiced
              ? "This property already has an open closing invoice, so no new invoice was sent. Continue with the client's setup."
              : inEscrow
                ? `Closing invoice saved, ${
                    buyerPays
                      ? "payable by the buyer through escrow"
                      : "payable from the agent's commission"
                  }. The escrow officer will issue it from Closing assignments. Protection will activate when EquityProtect marks the invoice paid.`
                : "Closing invoice created and emailed to you. Protection will activate when EquityProtect marks the invoice paid."
          }
        />
      ) : null}
    </Box>
  );
};

export default PaidAtClosingConfirm;
